import type { ChartOptions } from "chart.js";
import { isObject } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Breadcrumbs } from "src/components/Breadcrumb";
import { ReportIcon } from "src/components/ui-icons/sidebar";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import BarChart from "../IncidentsReport/BarChart";
import PaginationLeftSmallIcon from "../IncidentsReport/icon/PaginationLeftSmallIcon";
import PaginationRightSmallIcon from "../IncidentsReport/icon/PaginationRightSmallIcon";
import { selectItems } from "src/modules/spItems/selectors";
import { myTasks5WayStatusesListName, myTasksDepartmentsListName, myTasksDeviationReportDependListName, myTasksDeviationReportKpiByTriggerListName, myTasksDeviationReportTriggersListName, myTasksKpiByTriggerListName, myTasksKPIListName, myTasksLocationsListName, myTasksRegionsListName } from "src/api/config/myTasksLists.config";
import { getItemsRequest } from "src/modules/spItems/api";
import _ from "lodash";
import LoadingOpBg from "src/components/LoadingOpBg";
import Form from "src/components/Form";
import { myTasks5WayListName, myTasksSDCA5WayListName } from "src/api/config/myTasks5Way.config";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";
import Loading from "src/components/Loading";
import CogSearchIcon from "./icon/CogSearchIcon";
import { EmptyList } from "src/components/EmptyList";
import { actionsStatus, fiveWhyStatus } from "src/api/config/systemSwitch";

type FiveWhyReportStatusType = "months" | "factories" | "triggers";

const switchButtons: { type: FiveWhyReportStatusType; key: FiveWhyReportStatusType; title: string }[] = [
  { type: "months", key: "months", title: "Статусы по месяцам" },
  { type: "factories", key: "factories", title: "Статусы по заводам" },
  { type: "triggers", key: "triggers", title: "Частота срабатывания триггеров" },
];

const fiveWhyOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 8, // Size of the color box (rectangle)
        boxHeight: 8, // Height of the color box
        padding: 20, // Spacing between legend items
      },
    },
    tooltip: {
      callbacks: {
        label: (tooltipItem: any) => {
          return ` ${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
        },
      },
    },
    datalabels: {
      display: true,
      color: "#000", // Label color
      font: {
        size: 14, // Font size
      },
      align: "end", // Position above the bar
      anchor: "end", // Anchor to the end of the bar
      formatter: (value) => value, // Display the raw value
    },
  },
  scales: {
    x: {
      grid: {
        display: false, // Убирает сетку, чтобы это не влияло визуально
      },
    },
    y: {
      // stacked: true, // Убедитесь, что это не нарушает поведение оси Y
      beginAtZero: true,
      max: 100, // Устанавливаем максимальное значение оси Y
      ticks: {
        stepSize: 10, // Шаг шкалы кратен 10
        callback: (value: any) => `${value}`, // Отображение значений (например, можно добавить единицы измерения)
      },
    },
  },
};

type SortedFieldType = "countTrigger" | "titleTrigger";

function List5WayFilters({ setFilters, setGetList5Way, setLocalFilters, activeStatus, setYear, year }: any) {
  const [defaultValues, setDefaultValues] = useState({});
  const [fields, setFields] = useState<any>([
    {
      type: 'dateRange',
      require: false,
      key: 'Date5Way',
      title: 'Дата',
      disabled: true,
      isFilter: true,
      placeholder: 'дд.мм.гггг',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'Status',
      disabled: true,
      isFilter: true,
      title: 'Статус',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'Location',
      isFilter: true,
      searchInFilterCheckbox: true,
      title: 'Локация',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'Department',
      searchInFilterCheckbox: true,
      isFilter: true,
      title: 'Отдел',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'Region',
      searchInFilterCheckbox: true,
      title: 'Участок',
      isFilter: true,
      placeholder: 'Не выбрано',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'Trigger',
      searchInFilterCheckbox: true,
      isFilter: true,
      title: 'Триггер',
      placeholder: 'Не выбрано',
      width: 'w-full'
    },
    {
      type: 'checkbox',
      require: false,
      key: 'KPI',
      searchInFilterCheckbox: true,
      title: 'KPI',
      isFilter: true,
      placeholder: 'Не выбрано',
      width: 'w-full'
    },
    {
      type: "peoplePicker",
      require: false,
      single: true,
      isFilter: true,
      key: "AuthorEmail",
      title: "Автор",
      current: false,
      placeholder: "Введите имя или фамилию",
      width: "w-full",
    },
    {
      type: "peoplePicker",
      require: false,
      single: true,
      isFilter: true,
      key: "Editors",
      title: "Участник",
      current: false,
      placeholder: "Введите имя или фамилию",
      width: "w-full",
    },
    {
      type: 'radio',
      require: false,
      isFilter: true,
      key: 'ProblemRepeat',
      title: 'Повторяющаяся проблема?',
      width: 'w-full'
    },
    {
      type: 'radio',
      require: true,
      isFilter: true,
      key: 'Plan',
      title: 'План реагирования выполнен?',
      width: 'w-full'
    },
    {
      type: 'radio',
      require: true,
      isFilter: true,
      key: 'Tree',
      disabled: true,
      title: 'Корневая причина выявлена',
      width: 'w-full'
    },
    {
      type: 'radio',
      require: true,
      isFilter: true,
      key: 'ActionsPlan',
      title: 'Действия запланированы',
      width: 'w-full'
    }
  ]);
  const [list5WayFilter, setList5WayFilter] = useState<any>(null);

  const [dependenceFields, setDependenceFields] = useState<any>({
    Location: null,
    Department: null,
    Region: null,
    Trigger: null,
    KPI: null
  });

  const statuses: any = useAppSelector((state) => selectItems(state, myTasks5WayStatusesListName));
  const locations: any = useAppSelector((state) => selectItems(state, myTasksLocationsListName));
  const departments: any = useAppSelector((state) => selectItems(state, myTasksDepartmentsListName));
  const regions: any = useAppSelector((state) => selectItems(state, myTasksRegionsListName));
  const triggersDeviationReport: any = useAppSelector((state) => selectItems(state, myTasksDeviationReportTriggersListName));
  const kpiItems: any = useAppSelector((state) => selectItems(state, myTasksKPIListName));

  const dispatch = useAppDispatch();
  const [resetForm, setResetForm] = useState(false);
  const [customLoading, setCustomLoading] = useState(true);
  const [resetFilters, setResetFiters] = useState(false);

  //Формируем данные фильтры по умолчанию
  useEffect(() => {
    const tempFieldsForm = [...fields];
    let tempTriggers: any = [];
    tempTriggers = [...triggersDeviationReport];
    for (let i = 0; i < tempFieldsForm.length; i++) {
      if (tempFieldsForm[i]['key'] === 'Status') {
        tempFieldsForm[i]['optionsInputs'] = statuses.filter((status: any) => status.ID !== fiveWhyStatus.escalate).map((status: any) => {
          return {
            id: status.ID,
            name: status.Title,
            disabled: false
          }
        });
        if (activeStatus === 'triggers') {
          tempFieldsForm[i]['disabled'] = false;
        } else {
          tempFieldsForm[i]['disabled'] = true;
        }
      }
      if (tempFieldsForm[i]['key'] === 'Location') {
        tempFieldsForm[i]['optionsInputs'] = locations.map((location: any) => {
          return {
            id: location.ID,
            name: location.Title,
            disabled: false
          }
        })
      }
      if (tempFieldsForm[i]['key'] === 'Department') {
        tempFieldsForm[i]['optionsInputs'] = departments.map((department: any) => {
          return {
            id: department.ID,
            name: department.Title,
            disabled: false
          }
        })
      }
      if (tempFieldsForm[i]['key'] === 'Region') {
        tempFieldsForm[i]['optionsInputs'] = regions.map((region: any) => {
          return {
            id: region.ID,
            name: region.Title,
            disabled: false
          }
        })
      }
      if (tempFieldsForm[i]['key'] === 'Trigger') {
        tempFieldsForm[i]['optionsInputs'] = tempTriggers.map((trigger: any) => {
          return {
            id: trigger.ID,
            name: trigger.Title,
            disabled: false
          }
        })
      }
      if (tempFieldsForm[i]['key'] === 'KPI') {
        tempFieldsForm[i]['optionsInputs'] = kpiItems.map((kpiItem: any) => {
          return {
            id: kpiItem.ID,
            name: kpiItem.Title,
            disabled: false
          }
        })
      }
      if (tempFieldsForm[i]['key'] === 'Date5Way') {
        if (activeStatus === 'triggers' || activeStatus === 'factories') {
          tempFieldsForm[i]['disabled'] = false;
        } else {
          tempFieldsForm[i]['disabled'] = true;
        }
      }
      if (tempFieldsForm[i]['key'] === 'ProblemRepeat') {
        tempFieldsForm[i]['optionsInputs'] = ['Да', 'Нет']
      }
      if (tempFieldsForm[i]['key'] === 'Tree') {
        if (activeStatus === 'triggers') {
          tempFieldsForm[i]['disabled'] = false;
        } else {
          tempFieldsForm[i]['disabled'] = true;
        }
        tempFieldsForm[i]['optionsInputs'] = ['Да', 'Нет']
      }
      if (tempFieldsForm[i]['key'] === 'ActionsPlan') {
        tempFieldsForm[i]['optionsInputs'] = ['Да', 'Нет']
      }
      if (tempFieldsForm[i]['key'] === 'Plan') {
        tempFieldsForm[i]['optionsInputs'] = ['Да', 'Нет', 'План реагирования отсутствует']
      }
    }
    setFields([...tempFieldsForm]);
  }, [locations, statuses, departments, regions, triggersDeviationReport, kpiItems, activeStatus]);

  useEffect(() => {
    setCustomLoading(true);
    let filtersDepend5Way: any = {};
    let filtersDependKpi: any = {};
    const tempFieldsForm = [...fields];
    let tempTriggers: any = [];
    tempTriggers = [...triggersDeviationReport];
    Object.keys(dependenceFields).map(key => {
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]['key'] === key) {
          tempFieldsForm[i]['optionsInputs'] = tempFieldsForm[i]['optionsInputs'].map((option: any) => {
            return {
              id: option.id,
              name: option.name,
              disabled: false
            }
          })
        }
      }
    });
    Object.keys(dependenceFields).map(key => {
      if (dependenceFields[key] && dependenceFields[key].length > 0 && key !== 'KPI') {
        if (key === 'Department') {
          filtersDepend5Way['Departament'] = dependenceFields[key].map((item: any) => item.id);
        } else {
          filtersDepend5Way[key] = dependenceFields[key].map((item: any) => item.id);
        }
      } else if (key === 'KPI' && dependenceFields[key] && dependenceFields[key].length > 0) {
        filtersDependKpi['Kpi'] = dependenceFields[key].map((item: any) => item.id);
      }
    });
    if (Object.keys(filtersDependKpi).length === 0 && Object.keys(filtersDepend5Way).length === 0) {
      setFields([...tempFieldsForm]);
    } else {
      if (Object.keys(filtersDependKpi).length > 0 && Object.keys(filtersDepend5Way).length === 0) {
        setCustomLoading(true);
        getItemsRequest({ listName: myTasksDeviationReportKpiByTriggerListName, filters: filtersDependKpi }).then(results => {
          const dependKpiByTrigger = [...results.items];
          const triggerIds = _.uniqWith(dependKpiByTrigger.map((item: any) => item.Trigger.get_lookupId()), _.isEqual);
          for (let i = 0; i < tempFieldsForm.length; i++) {
            if (tempFieldsForm[i]['key'] === 'Trigger') {
              tempFieldsForm[i]['optionsInputs'] = tempTriggers.map((trigger: any) => {
                return {
                  id: trigger.ID,
                  name: trigger.Title,
                  disabled: false, // triggerIds.indexOf(trigger.ID) === -1,
                  hide: triggerIds.indexOf(trigger.ID) === -1
                }
              })
            }
          }
          setFields([...tempFieldsForm]);
        })
      }
      if (Object.keys(filtersDepend5Way).length > 0) {
        setCustomLoading(true);
        //Устанавливаем зависимости в фильтрах 
        getItemsRequest({ listName: myTasksDeviationReportDependListName, filters: filtersDepend5Way, filtersType: "or" }).then(results => {
          const depend5Way = [...results.items];
          const locationIds = _.uniqWith(depend5Way.map((item: any) => item.Location.get_lookupId()), _.isEqual);
          const departmentIds = _.uniqWith(depend5Way.map((item: any) => item.Departament.get_lookupId()), _.isEqual);
          const regionIds = _.uniqWith(depend5Way.map((item: any) => item.Region.get_lookupId()), _.isEqual);
          const triggerIds = _.uniqWith(depend5Way.map((item: any) => item.Trigger.get_lookupId()), _.isEqual);
          for (let i = 0; i < tempFieldsForm.length; i++) {
            if (tempFieldsForm[i]['key'] === 'Location') {
              if (!filtersDepend5Way['Location'] || Object.keys(filtersDepend5Way).length > 1) {
                tempFieldsForm[i]['optionsInputs'] = locations.map((location: any) => {
                  return {
                    id: location.ID,
                    name: location.Title,
                    disabled: false, // locationIds.indexOf(location.ID) === -1,
                    hide: locationIds.indexOf(location.ID) === -1
                  }
                })
              }
            }
            if (tempFieldsForm[i]['key'] === 'Department') {
              if (!filtersDepend5Way['Departament'] || Object.keys(filtersDepend5Way).length > 1) {
                tempFieldsForm[i]['optionsInputs'] = departments.map((department: any) => {
                  return {
                    id: department.ID,
                    name: department.Title,
                    disabled: false, // departmentIds.indexOf(department.ID) === -1,
                    hide: departmentIds.indexOf(department.ID) === -1
                  }
                })
              }
            }
            if (tempFieldsForm[i]['key'] === 'Region') {
              if (!filtersDepend5Way['Region'] || Object.keys(filtersDepend5Way).length > 1) {
                tempFieldsForm[i]['optionsInputs'] = regions.map((region: any) => {
                  return {
                    id: region.ID,
                    name: region.Title,
                    disabled: false, // regionIds.indexOf(region.ID) === -1
                    hide: regionIds.indexOf(region.ID) === -1
                  }
                })
              }
            }
            if (tempFieldsForm[i]['key'] === 'Trigger') {
              if (!filtersDepend5Way['Trigger'] || Object.keys(filtersDepend5Way).length > 1) {
                tempFieldsForm[i]['optionsInputs'] = tempTriggers.map((trigger: any) => {
                  return {
                    id: trigger.ID,
                    name: trigger.Title,
                    disabled: false, // triggerIds.indexOf(trigger.ID) === -1,
                    hide: triggerIds.indexOf(trigger.ID) === -1
                  }
                })
              }
            }
          }
          if (triggerIds.length > 0) {
            getItemsRequest({ listName: myTasksKpiByTriggerListName, filters: { Trigger: triggerIds }, method: '/api/deviation-report-kpi-by-triggers/search' }).then(results => {
              const dependKpiByTrigger = [...results.items];
              const kpiIds = _.uniqWith(dependKpiByTrigger.map((item: any) => item.Kpi.get_lookupId()), _.isEqual);
              for (let i = 0; i < tempFieldsForm.length; i++) {
                if (tempFieldsForm[i]['key'] === 'KPI') {
                  tempFieldsForm[i]['optionsInputs'] = kpiItems.map((kpiItem: any) => {
                    return {
                      id: kpiItem.ID,
                      name: kpiItem.Title,
                      disabled: false, // kpiIds.indexOf(kpiItem.ID) === -1,
                      hide: kpiIds.indexOf(kpiItem.ID) === -1
                    }
                  })
                }
              }
              setFields([...tempFieldsForm]);
            });
          } else {
            setFields([...tempFieldsForm]);
          }
        });
      }
    }
  }, [dependenceFields]);

  useEffect(() => {
    setDependenceFields({
      Location: null,
      Department: null,
      Region: null,
      Trigger: null,
      KPI: null
    })
  }, [resetFilters]);

  useEffect(() => {
    if (list5WayFilter && Object.keys(list5WayFilter).length > 0) {
      const existsFilters5Way: any = {};
      Object.keys(list5WayFilter).map((key) => {
        if (list5WayFilter[key]) {
          if (Array.isArray(list5WayFilter[key])) {
            if (list5WayFilter[key].length > 0) {
              existsFilters5Way[key] = list5WayFilter[key];
            }
          } else if (isObject(list5WayFilter[key])) {
            if (Object.keys(list5WayFilter[key]).length > 0) {
              existsFilters5Way[key] = list5WayFilter[key];
            }
          } else {
            existsFilters5Way[key] = list5WayFilter[key];
          }
        }
      });
      const tempFilters: any = {};
      let temp5WayLocalFilter: any = [];
      Object.keys(existsFilters5Way).map((key) => {
        if (key === "Date5Way") {
          tempFilters["Date5Way"] = {
            start: moment(existsFilters5Way[key].startDate).format('YYYY-MM-DD'),
            end: moment(existsFilters5Way[key].endDate).format('YYYY-MM-DD')
          };
        } else if (key === "Location" || key === "Department" || key === "Region" || key === "Status" || key === "Trigger" || key === "KPI") {
          tempFilters[key] = existsFilters5Way[key].map((item: any) => item.id);
        } else if (key === 'AuthorEmail' || key === 'Editors') {
          tempFilters[key] = `${existsFilters5Way[key][0]["value"]}*`;
        } else if (key === 'Tree') {
          temp5WayLocalFilter = [...temp5WayLocalFilter, { tree: existsFilters5Way[key] }]
        } else if (key === 'ProblemRepeat') {
          if (existsFilters5Way[key] === 'Да') {
            tempFilters[key] = true;
          } else {
            tempFilters[key] = false;
          }
        } else if (key === 'ActionsPlan') {
          temp5WayLocalFilter = [...temp5WayLocalFilter, { actionsPlan: existsFilters5Way[key] }]
        } else {
          tempFilters[key] = existsFilters5Way[key]
        }
      });
      setLocalFilters(temp5WayLocalFilter);
      setGetList5Way(false);
      tempFilters['Type5Way'] = 'DeviationReport';
      setFilters({ ...tempFilters });
      setResetForm(false);
    } else {
      setResetForm(true);
    }
  }, [list5WayFilter]);

  useEffect(() => {
    setCustomLoading(false);
  }, [fields]);

  useEffect(() => {
    if (activeStatus === 'months') {
      let defaultObj: any = {};
      fields.map((field: any) => {
        if (field.key === 'Date5Way') {
          defaultObj[field.key] = {
            startDate: moment().startOf('year').toDate(),
            endDate: moment().endOf('year').toDate()
          }
        } else if (field.key === 'Status') {
          defaultObj[field.key] = [{
            id: fiveWhyStatus.process,
            name: 'В процессе'
          },
          {
            id: fiveWhyStatus.closed,
            name: 'Закрыто'
          },
          {
            id: fiveWhyStatus.old,
            name: 'Просрочено'
          }]
        } else {
          defaultObj[field.key] = null
        }
      })
      setDefaultValues(defaultObj);
    }
    if (activeStatus === 'factories') {
      let defaultObj: any = {};
      fields.map((field: any) => {
        if (field.key === 'Date5Way') {
          defaultObj[field.key] = {
            startDate: moment().startOf('month').toDate(),
            endDate: moment().endOf('month').toDate()
          }
        } else if (field.key === 'Status') {
          defaultObj[field.key] = [{
            id: fiveWhyStatus.process,
            name: 'В процессе'
          },
          {
            id: fiveWhyStatus.closed,
            name: 'Закрыто'
          },
          {
            id: fiveWhyStatus.old,
            name: 'Просрочено'
          }]
        } else {
          defaultObj[field.key] = null
        }
      })
      setDefaultValues(defaultObj);
    }
    if (activeStatus === 'triggers') {
      let defaultObj: any = {};
      fields.map((field: any) => {
        if (field.key === 'Date5Way') {
          defaultObj[field.key] = {
            startDate: moment().startOf('month').toDate(),
            endDate: moment().endOf('month').toDate()
          }
        } else {
          defaultObj[field.key] = null
        }
      })
      setDefaultValues(defaultObj);
    }
    setYear(moment());
  }, [activeStatus, resetFilters]);

  useEffect(() => {
    if (activeStatus === 'months') {
      setDefaultValues((prevState: any) => {
        return {
          ...prevState, Date5Way: {
            startDate: year.startOf('year').toDate(),
            endDate: year.endOf('year').toDate()
          }
        };
      });
    }
  }, [year]);

  return (
    <FlexCol className="relative">
      {customLoading && <LoadingOpBg />}
      {list5WayFilter && Object.keys(list5WayFilter).length > 0 && (
        <div
          onClick={() => {
            setLocalFilters({});
            setGetList5Way(false);
            setList5WayFilter(null);
            setResetFiters(!resetFilters)
          }}
          className="p-5 flex flex-row group items-center text-[14px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap border-b"
        >
          <div className="flex flex-col">Сбросить фильтры</div>
          <div className="flex flex-col pt-1">
            <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_33_4954)">
                <path
                  d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                  fill="#637381"
                ></path>
              </g>
              <defs>
                <clipPath id="clip0_33_4954">
                  <rect width="16" height="16" fill="white" transform="translate(0 12) rotate(-90)"></rect>
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      )}
      <Form
        type="list5way_filters_my_tasks"
        setFormData={setList5WayFilter}
        fieldsForm={fields}
        defaultValues={defaultValues}
        resetForm={resetForm}
        setDependenceFields={setDependenceFields}
      />
      {list5WayFilter && Object.keys(list5WayFilter).length > 0 && (
        <div
          onClick={() => {
            setLocalFilters({});
            setGetList5Way(false);
            setList5WayFilter(null);
            setResetFiters(!resetFilters)
          }}
          className="p-5 flex flex-row group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
        >
          <div className="flex flex-col group-hover:underline">Сбросить фильтры</div>
          <div className="flex flex-col pt-1">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_33_4954)">
                <path
                  d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                  fill="#637381"
                ></path>
              </g>
              <defs>
                <clipPath id="clip0_33_4954">
                  <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)"></rect>
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      )}
    </FlexCol>
  );
}

function FiveWhyReport() {
  const [activeStatus, setActiveStatus] = useState<FiveWhyReportStatusType>("months");
  const [sortedField, setSortedField] = useState<SortedFieldType>("titleTrigger");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");
  const triggers: any = useAppSelector((state) => selectItems(state, myTasksDeviationReportTriggersListName));
  const [chartYearData, setChartYearData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: "Создано",
        data: [],
        backgroundColor: "#00629B", // Желтый
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "Закрыто",
        data: [],
        backgroundColor: "#82E6AC", // Зеленый
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "В процессе (корневая причина найдена)",
        data: [],
        backgroundColor: "#FEF3C7", // Красный
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "В процессе (корневая причина не найдена)",
        data: [],
        backgroundColor: "#FBD5C0", // Синий
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
    ],
  })
  const [chartFactoryData, setChartFactoryData] = useState<any>({
    labels: [],
    datasets: [
      {
        label: "Создано",
        data: [],
        backgroundColor: "#00629B", // Желтый
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "Закрыто",
        data: [],
        backgroundColor: "#82E6AC", // Зеленый
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "В процессе (корневая причина найдена)",
        data: [],
        backgroundColor: "#FEF3C7", // Красный
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
      {
        label: "В процессе (корневая причина не найдена)",
        data: [],
        backgroundColor: "#FBD5C0", // Синий
        borderRadius: 10,
        barPercentage: 1, // 30px width for each bar
        categoryPercentage: 0.85, // Controls spacing between groups of bars
      },
    ],
  })
  const [chartTriggersData, setChartTriggersData] = useState<any>({});
  const [sortTriggersData, setSortTriggersData] = useState<any>({});

  const [year, setYear] = useState(moment());

  const [filters, setFilters] = useState<any>({});
  const [localFilters, setLocalFilters] = useState<any>([]);
  const [nextPage, setNextPage] = useState<any>(null);
  const [list5Way, setList5Way] = useState<any>([]);
  const [tempList5Way, setTempList5Way] = useState<any>([]);
  const dispatch = useAppDispatch();
  const [customLoading, setCustomLoading] = useState(true);
  const [getList5Way, setGetList5Way] = useState(false);
  const [factoriesLabels, setFactoriesLabels] = useState<any>([]);
  const [monthLabels, setMonthLabels] = useState<any>([]);
  const months = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

  function changeStatus(type: FiveWhyReportStatusType) {
    setActiveStatus(type);
  }

  const handleSort = (field: SortedFieldType) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) => prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc");
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    if (Object.keys(chartTriggersData).length > 0) {
      if (sortedField === 'titleTrigger') {
        if (sortDirection === 'default') {
          setSortTriggersData(chartTriggersData);
        }
        if (sortDirection === 'asc') {
          setSortTriggersData(Object.keys(chartTriggersData)
            .sort((a, b) => b.toLowerCase().localeCompare(a.toLowerCase()))
            .reduce((acc: any, key: any) => {
              acc[key] = chartTriggersData[key];
              return acc;
            }, {}))
        }
        if (sortDirection === 'desc') {
          setSortTriggersData(Object.keys(chartTriggersData)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .reduce((acc: any, key: any) => {
              acc[key] = chartTriggersData[key];
              return acc;
            }, {}))
        }
      }
      if (sortedField === 'countTrigger') {
        if (sortDirection === 'default') {
          setSortTriggersData(chartTriggersData);
        }
        if (sortDirection === 'asc') {
          setSortTriggersData(Object.fromEntries(
            Object.entries(chartTriggersData).sort((a: any, b: any) => b[1].length - a[1].length)
          ))
        }
        if (sortDirection === 'desc') {
          setSortTriggersData(Object.fromEntries(
            Object.entries(chartTriggersData).sort((a: any, b: any) => a[1].length - b[1].length)
          ))

        }
      }
    }
  }, [sortedField, sortDirection, chartTriggersData]);

  //Получение 5Почему после изменения фильтров
  useEffect(() => {
    if (!getList5Way && Object.keys(filters).length > 0) {
      setCustomLoading(true);
      setChartYearData({
        labels: [],
        datasets: [
          {
            label: "Создано",
            data: [],
            backgroundColor: "#00629B", // Желтый
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "Закрыто",
            data: [],
            backgroundColor: "#82E6AC", // Зеленый
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "В процессе (корневая причина найдена)",
            data: [],
            backgroundColor: "#FEF3C7", // Красный
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "В процессе (корневая причина не найдена)",
            data: [],
            backgroundColor: "#FBD5C0", // Синий
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
        ],
      });
      setChartFactoryData({
        labels: [],
        datasets: [
          {
            label: "Создано",
            data: [],
            backgroundColor: "#00629B", // Желтый
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "Закрыто",
            data: [],
            backgroundColor: "#82E6AC", // Зеленый
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "В процессе (корневая причина найдена)",
            data: [],
            backgroundColor: "#FEF3C7", // Красный
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
          {
            label: "В процессе (корневая причина не найдена)",
            data: [],
            backgroundColor: "#FBD5C0", // Синий
            borderRadius: 10,
            barPercentage: 1, // 30px width for each bar
            categoryPercentage: 0.85, // Controls spacing between groups of bars
          },
        ],
      });
      setChartTriggersData({});
      setSortTriggersData({});
      setTempList5Way([]);
      setList5Way([]);
      setMonthLabels([]);
      setFactoriesLabels([]);
      getItemsRequest({
        listName: myTasks5WayListName,
        rowLimit: 25,
        filters,
        sortOrder: { name: "ID", desc: true },
      }).then((results: any) => {
        const { items, nextPage } = results;
        let list5WayIds: string[] = [];
        items.map((item: any) => {
          list5WayIds = [...list5WayIds, item.ID];
        });
        if (list5WayIds.length > 0) {
          getItemsRequest({ listName: myTasksSDCA5WayListName, filters: { _x0035_Way: list5WayIds }, method: '/api/sdca5-way/search' }).then(results => {
            const sdcaItems = [...results.items];
            let arrRequests: any = [];
            sdcaItems.map(item => {
              arrRequests = [...arrRequests, getItemsRequest({ listName: myTaskActionsListName, filters: { ImportantHashTag: `#Отчет_об_отклонениях_${item.ID}*` } })];
            });
            Promise.all(arrRequests).then(values => {
              let actions: any = [];
              values.map(value => {
                actions = [...actions, ...value.items];
              });
              const returnItems = items.map((item: any) => {
                const currentSdcaItems = sdcaItems.filter((sdcaItem: any) => sdcaItem._x0035_Way.get_lookupId() === item.ID);
                let currentActions: any = [];
                currentSdcaItems.map((currentSdcaItem: any) => {
                  currentActions = [...currentActions, ...actions.filter(
                    (action: any) => action.ImportantHashTag.indexOf(`#Отчет_об_отклонениях_${currentSdcaItem.ID}`) !== -1
                  )]
                });
                return {
                  ...item,
                  actions: currentActions,
                  actionsPlan: currentActions.length > 0,
                  actionsClosed:
                    currentActions.length > 0 &&
                    !currentActions.some((action: any) => action.Status.get_lookupId() !== actionsStatus.closed),
                };
              });
              setTempList5Way([...returnItems]);
              setGetList5Way(true);
              if (items.length === 25) {
                setNextPage(nextPage);
              } else {
                setNextPage(false);
              }
            });
          });
        } else {
          setTempList5Way([]);
          setList5Way([]);
          setGetList5Way(true);
          setNextPage(false);
        }
      });
    }
  }, [filters]);

  //Получение 5Почему по nextPage
  useEffect(() => {
    if (nextPage && getList5Way) {
      getItemsRequest({
        listName: myTasks5WayListName,
        rowLimit: 25,
        filters,
        nextPage,
        sortOrder: { name: "ID", desc: true },
      }).then((results: any) => {
        const { items, nextPage } = results;
        let list5WayIds: string[] = [];
        items.map((item: any) => {
          list5WayIds = [...list5WayIds, item.ID];
        });

        if (list5WayIds.length > 0) {
          getItemsRequest({ listName: myTasksSDCA5WayListName, filters: { _x0035_Way: list5WayIds }, method: '/api/sdca5-way/search' }).then(results => {
            const sdcaItems = [...results.items];
            let arrRequests: any = [];
            sdcaItems.map(item => {
              arrRequests = [...arrRequests, getItemsRequest({ listName: myTaskActionsListName, filters: { ImportantHashTag: `#Отчет_об_отклонениях_${item.ID}*` } })];
            });
            Promise.all(arrRequests).then(values => {
              let actions: any = [];
              values.map(value => {
                actions = [...actions, ...value.items];
              });
              const returnItems = items.map((item: any) => {
                const currentSdcaItems = sdcaItems.filter((sdcaItem: any) => sdcaItem._x0035_Way.get_lookupId() === item.ID);
                let currentActions: any = [];
                currentSdcaItems.map((currentSdcaItem: any) => {
                  currentActions = [...currentActions, ...actions.filter(
                    (action: any) => action.ImportantHashTag.indexOf(`#Отчет_об_отклонениях_${currentSdcaItem.ID}`) !== -1
                  )]
                });
                return {
                  ...item,
                  actions: currentActions,
                  actionsPlan: currentActions.length > 0,
                  actionsClosed:
                    currentActions.length > 0 &&
                    !currentActions.some((action: any) => action.Status.get_lookupId() !== actionsStatus.closed),
                };
              });
              setTempList5Way((prevState: any) => [...prevState, ...returnItems]);
              if (items.length === 25) {
                setNextPage(nextPage);
              } else {
                setNextPage(false);
              }
            });
          });
        }
      });
    }
  }, [nextPage]);

  //Локальная фильтрация
  useEffect(() => {
    if (localFilters.length > 0) {
      let tempLocalData: any = [...tempList5Way];

      let responsible = null, tree = null, actionsPlan = null;
      if (localFilters.find((item: any) => item.hasOwnProperty('responsible'))) {
        responsible = localFilters.find((item: any) => item.hasOwnProperty('responsible'))['responsible'];
      }
      if (localFilters.find((item: any) => item.hasOwnProperty('tree'))) {
        tree = localFilters.find((item: any) => item.hasOwnProperty('tree'))['tree'];
      }
      if (localFilters.find((item: any) => item.hasOwnProperty('actionsPlan'))) {
        actionsPlan = localFilters.find((item: any) => item.hasOwnProperty('actionsPlan'))['actionsPlan'];
      }
      if (responsible) {
        tempLocalData = tempLocalData.filter((item: any) => item.Editors && item.Editors.split(";")[item.Editors.split(";").length - 1] === responsible);
        if (tree) {
          if (tree === 'Да') {
            tempLocalData = tempLocalData.filter((item: any) => item.Tree && item.Tree.indexOf(`"root":true`) !== -1);
          } else {
            tempLocalData = tempLocalData.filter((item: any) => !item.Tree || (item.Tree && item.Tree.indexOf(`"root":true`) === -1));
          }
          if (actionsPlan) {
            if (actionsPlan === 'Да') {
              tempLocalData = tempLocalData.filter((item: any) => item.actionsPlan)
            } else {
              tempLocalData = tempLocalData.filter((item: any) => !item.actionsPlan)
            }
          }
        }
        if (actionsPlan) {
          if (actionsPlan === 'Да') {
            tempLocalData = tempLocalData.filter((item: any) => item.actionsPlan)
          } else {
            tempLocalData = tempLocalData.filter((item: any) => !item.actionsPlan)
          }
          if (tree) {
            if (tree === 'Да') {
              tempLocalData = tempLocalData.filter((item: any) => item.Tree && item.Tree.indexOf(`"root":true`) !== -1);
            } else {
              tempLocalData = tempLocalData.filter((item: any) => !item.Tree || (item.Tree && item.Tree.indexOf(`"root":true`) === -1));
            }
          }
        }
      }
      if (tree) {
        if (tree === 'Да') {
          tempLocalData = tempLocalData.filter((item: any) => item.Tree && item.Tree.indexOf(`"root":true`) !== -1);
        } else {
          tempLocalData = tempLocalData.filter((item: any) => !item.Tree || (item.Tree && item.Tree.indexOf(`"root":true`) === -1));
        }
        if (actionsPlan) {
          if (actionsPlan === 'Да') {
            tempLocalData = tempLocalData.filter((item: any) => item.actionsPlan)
          } else {
            tempLocalData = tempLocalData.filter((item: any) => !item.actionsPlan)
          }
          if (responsible) {
            tempLocalData = tempLocalData.filter((item: any) => item.Editors && item.Editors.split(";")[item.Editors.split(";").length - 1] === responsible);
          }
        }
        if (responsible) {
          tempLocalData = tempLocalData.filter((item: any) => item.Editors && item.Editors.split(";")[item.Editors.split(";").length - 1] === responsible);

          if (actionsPlan) {
            if (actionsPlan === 'Да') {
              tempLocalData = tempLocalData.filter((item: any) => item.actionsPlan)
            } else {
              tempLocalData = tempLocalData.filter((item: any) => !item.actionsPlan)
            }
          }
        }

      }
      if (actionsPlan) {
        if (actionsPlan === 'Да') {
          tempLocalData = tempLocalData.filter((item: any) => item.actionsPlan)
        } else {
          tempLocalData = tempLocalData.filter((item: any) => !item.actionsPlan)
        }

        if (tree) {
          if (tree === 'Да') {
            tempLocalData = tempLocalData.filter((item: any) => item.Tree && item.Tree.indexOf(`"root":true`) !== -1);
          } else {
            tempLocalData = tempLocalData.filter((item: any) => !item.Tree || (item.Tree && item.Tree.indexOf(`"root":true`) === -1));
          }
          if (responsible) {
            tempLocalData = tempLocalData.filter((item: any) => item.Editors && item.Editors.split(";")[item.Editors.split(";").length - 1] === responsible);
          }
        }
        if (responsible) {
          tempLocalData = tempLocalData.filter((item: any) => item.Editors && item.Editors.split(";")[item.Editors.split(";").length - 1] === responsible);

          if (tree) {
            if (tree === 'Да') {
              tempLocalData = tempLocalData.filter((item: any) => item.Tree && item.Tree.indexOf(`"root":true`) !== -1);
            } else {
              tempLocalData = tempLocalData.filter((item: any) => !item.Tree || (item.Tree && item.Tree.indexOf(`"root":true`) === -1));
            }
          }
        }
      }
      setList5Way([..._.uniqWith(tempLocalData, _.isEqual)]);
    } else {
      setList5Way([..._.uniqWith(tempList5Way, _.isEqual)]);
    }
  }, [tempList5Way, localFilters]);

  useEffect(() => {
    let dataSetYearStatus: any = {
      created: [],
      closed: [],
      processWithRoot: [],
      processWithoutRoot: []
    };
    let dataSetFactoryStatus: any = {
      created: [],
      closed: [],
      processWithRoot: [],
      processWithoutRoot: []
    };
    let dataSetMonth: any = {};
    let dataSetFactory: any = {};
    let dataSetTriggers: any = {};
    if (activeStatus === 'factories') {
      list5Way.sort((a: any, b: any) => a.Location.get_lookupValue().localeCompare(b.Location.get_lookupValue()));
    }
    if (activeStatus === 'months') {
      list5Way.sort((a: any, b: any) => Number(moment(a.Date5Way).format('M')) < Number(moment(b.Date5Way).format('M')));
    }
    list5Way.map((item: any) => {
      if (!Array.isArray(dataSetMonth[item.Date5Way.getMonth() + 1])) {
        dataSetMonth[item.Date5Way.getMonth() + 1] = [];
      }
      if (!Array.isArray(dataSetFactory[item.Location.get_lookupValue()])) {
        dataSetFactory[item.Location.get_lookupValue()] = [];
      }
      if (triggers.length > 0 && item.Trigger && triggers.find((trigger: any) => trigger.ID === item.Trigger) && !Array.isArray(dataSetTriggers[triggers.find((trigger: any) => trigger.ID === item.Trigger)['Title']])) {
        dataSetTriggers[triggers.find((trigger: any) => trigger.ID === item.Trigger)['Title']] = [];
      }
      dataSetMonth[item.Date5Way.getMonth() + 1] = [...dataSetMonth[item.Date5Way.getMonth() + 1], item];
      dataSetFactory[item.Location.get_lookupValue()] = [...dataSetFactory[item.Location.get_lookupValue()], item];
      if (triggers.find((trigger: any) => trigger.ID === item.Trigger)) {
        dataSetTriggers[triggers.find((trigger: any) => trigger.ID === item.Trigger)['Title']] = [...dataSetTriggers[triggers.find((trigger: any) => trigger.ID === item.Trigger)['Title']], item];
      }
    });

    dataSetYearStatus['created'] = Object.keys(dataSetMonth).map(key => dataSetMonth[key].length);
    dataSetYearStatus['closed'] = Object.keys(dataSetMonth).map(key => dataSetMonth[key].filter((item: any) => item.Status && item.Status.get_lookupId() === fiveWhyStatus.closed).length);
    dataSetYearStatus['processWithRoot'] = Object.keys(dataSetMonth).map(key => dataSetMonth[key].filter((item: any) => item.Status && (item.Status.get_lookupId() === fiveWhyStatus.process || item.Status.get_lookupId() === fiveWhyStatus.old) && item.Tree && item.Tree.indexOf(`"root":true`) !== -1).length);
    dataSetYearStatus['processWithoutRoot'] = Object.keys(dataSetMonth).map(key => dataSetMonth[key].filter((item: any) => item.Status && (item.Status.get_lookupId() === fiveWhyStatus.process || item.Status.get_lookupId() === fiveWhyStatus.old) && (!item.Tree || item.Tree.indexOf(`"root":true`) === -1)).length);

    let tempFactoryLabels: any = [];
    Object.keys(dataSetFactory).map((key: string) => {
      tempFactoryLabels = [...tempFactoryLabels, key];
    });

    dataSetFactoryStatus['created'] = Object.keys(dataSetFactory).map(key => dataSetFactory[key].length);
    dataSetFactoryStatus['closed'] = Object.keys(dataSetFactory).map(key => dataSetFactory[key].filter((item: any) => item.Status && item.Status.get_lookupId() === fiveWhyStatus.closed).length);
    dataSetFactoryStatus['processWithRoot'] = Object.keys(dataSetFactory).map(key => dataSetFactory[key].filter((item: any) => item.Status && (item.Status.get_lookupId() === fiveWhyStatus.process || item.Status.get_lookupId() === fiveWhyStatus.old) && item.Tree && item.Tree.indexOf(`"root":true`) !== -1).length);
    dataSetFactoryStatus['processWithoutRoot'] = Object.keys(dataSetFactory).map(key => dataSetFactory[key].filter((item: any) => item.Status && (item.Status.get_lookupId() === fiveWhyStatus.process || item.Status.get_lookupId() === fiveWhyStatus.old) && (!item.Tree || item.Tree.indexOf(`"root":true`) === -1)).length);

    let tempChartYearData = { ...chartYearData };
    Object.keys(dataSetYearStatus).map((key, idx) => {
      tempChartYearData['datasets'][idx]['data'] = dataSetYearStatus[key];
    });

    let tempChartFactoryData = { ...chartFactoryData };
    Object.keys(dataSetFactoryStatus).map((key, idx) => {
      tempChartFactoryData['datasets'][idx]['data'] = dataSetFactoryStatus[key];
    });

    let tempMonthLabels: any = [];
    Object.keys(dataSetMonth).map((key: string) => {
      tempMonthLabels = [...tempMonthLabels, months[Number(key) - 1]];
    });

    if (!nextPage && getList5Way) {
      setChartYearData({ ...tempChartYearData });
      setFactoriesLabels([...tempFactoryLabels]);
      setChartFactoryData({ ...tempChartFactoryData });
      setChartTriggersData({ ...dataSetTriggers });
      setMonthLabels([...tempMonthLabels]);
      setCustomLoading(false);
    }
  }, [list5Way, triggers]);

  return (
    <FlexRow className="scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-screen overflow-y-auto">
      <FlexCol className="bg-white sticky-custom border-r w-80 text-[14px] text-[#8899A8] relative scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] max-h-screen">
        <FlexCol className="max-w-[320px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
          <List5WayFilters
            setGetList5Way={setGetList5Way}
            setFilters={setFilters}
            setLocalFilters={setLocalFilters}
            year={year}
            setYear={setYear}
            activeStatus={activeStatus}
          />
        </FlexCol>
      </FlexCol>
      <FlexCol className="grow w-[calc(100%-320px)]">
        <FlexCol className="w-full">
          <FlexRow className="px-7.5 h-[76px] border-b justify-between items-center shrink-0">
            <FlexCol>
              <Breadcrumbs
                items={[
                  {
                    name: "Отчеты",
                    icon: <ReportIcon />,
                  },
                  {
                    name: "Отчет об отклонении",
                    icon: <CogSearchIcon />,
                  },
                ]}
              />
            </FlexCol>
          </FlexRow>

          <FlexRow className="m-7.5 mb-2.5 shrink-0 flex-wrap">
            <FlexCol className="w-full space-y-2.5">
              <FlexRow className="items-center justify-between space-x-[5px] p-[5px] bg-white rounded-lg table-custom shadow-custom">
                {switchButtons.map((button) => (
                  <FlexCol
                    className={twMerge(
                      "flex-1 text-center font-medium cursor-pointer py-2.5 hover:bg-gray-50 rounded hover:text-darkblue",
                      activeStatus === button.type ? "text-darkblue bg-gray-50" : ""
                    )}
                    key={button.key}
                    onClick={() => changeStatus(button.type)}
                  >
                    {button.title}
                  </FlexCol>
                ))}
              </FlexRow>
              {customLoading &&
                <FlexCol className="justify-center items-center w-full">
                  <span className="block mb-5 text-base text-darkblue">Получаем данные...</span>
                  <Loading />
                </FlexCol>
                ||
                <>
                  {activeStatus === "months" && (
                    <BarChart
                      data={{ ...chartYearData, labels: monthLabels }}
                      options={fiveWhyOptions}
                      title={
                        <FlexRow className="absolute translate-x-[-50%] translate-y-[-50%] top-[1%] left-[50%]">
                          <FlexCol
                            className="hover:text-darkblue hover:cursor-pointer"
                            onClick={() => setYear((prev) => moment(prev).subtract(1, "year"))}
                          >
                            <PaginationLeftSmallIcon />
                          </FlexCol>
                          <FlexCol className="text-black select-none mt-[2px]">{moment(year).format("YYYY")}</FlexCol>
                          <FlexCol
                            className="hover:text-darkblue hover:cursor-pointer"
                            onClick={() => setYear((prev) => moment(prev).add(1, "year"))}
                          >
                            <PaginationRightSmallIcon />
                          </FlexCol>
                        </FlexRow>
                      }
                    />
                  )}
                  {activeStatus === "factories" && (
                    <BarChart
                      data={{ ...chartFactoryData, labels: factoriesLabels }}
                      options={fiveWhyOptions}
                      title={<FlexCol className="text-black"><></></FlexCol>}
                    />
                  )}
                  {activeStatus === "triggers" && (
                    <FlexRow className="px-5 py-6 bg-white rounded-lg table-custom shadow-custom">
                      <FlexCol className="flex-1">
                        <FlexRow className="items-center mb-5">
                          <FlexCol className="flex-1">
                            <FlexRow className="gap-x-2 justify-center">
                              <FlexCol className="font-medium whitespace-pre-line w-fit">Количество срабатываний</FlexCol>

                              <FlexCol className="w-auto cursor-pointer" onClick={() => handleSort("countTrigger")}>
                                <SortIcon sortDirection={sortedField === "countTrigger" ? sortDirection : "default"} />
                              </FlexCol>
                            </FlexRow>
                          </FlexCol>

                          <FlexCol className="flex-1">
                            <FlexRow className="gap-x-2 justify-center">
                              <FlexCol className="font-medium whitespace-pre-line w-fit">Триггер</FlexCol>

                              <FlexCol className="w-auto cursor-pointer" onClick={() => handleSort("titleTrigger")}>
                                <SortIcon sortDirection={sortedField === "titleTrigger" ? sortDirection : "default"} />
                              </FlexCol>
                            </FlexRow>
                          </FlexCol>
                        </FlexRow>
                        <FlexRow className="flex-wrap">
                          <FlexCol className="flex-1 gap-y-5">
                            {Object.keys(sortTriggersData).length > 0 && Object.keys(sortTriggersData).map((key: any) => (
                              <FlexRow className="items-top space-x-2.5">
                                <FlexCol className="flex-1">
                                  <TGRange percent={String((sortTriggersData[key].length / list5Way.length) * 100)} title={sortTriggersData[key].length} color="#2D68F8" />
                                </FlexCol>
                                <FlexCol className="flex-1">
                                  {key}
                                </FlexCol>
                              </FlexRow>
                            )) ||
                              <FlexCol className="justify-center items-center w-full h-full">
                                <EmptyList title="Данные не найдены" />
                              </FlexCol>
                            }
                          </FlexCol>
                        </FlexRow>
                      </FlexCol>
                    </FlexRow>
                  )}
                </>
              }
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexCol>
    </FlexRow>
  );
}

function TGRange({ percent, title, color = "blue" }: { percent: string; title: string; color: string }) {
  return (
    <div className="overflow-hidden relative w-full rounded-lg">
      <div className="w-full h-10 bg-gray-100 rounded-lg"></div>
      <div
        style={{
          width: `${percent}%`,
          transition: "width 0.5s ease-in-out",
          backgroundColor: color,
        }}
        className="flex absolute top-0 right-0 justify-center items-center h-10 text-sm text-white rounded-lg"
      >
        {title}
      </div>
    </div>
  );
}

export default FiveWhyReport;
